import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  createEvent,
  deleteEvent,
  getAllEvents,
  toggleEventActive,
  updateEvent,
  updateEventImage,
} from "../../../services/eventServices";

import { getResultsByEventId } from "../../../services/resultsEventServices";

import useScrollToTop from "../../../hooks/useScollToTop";

import { useNotification } from "../../../components/Notification/NotificationManager";

import EventFormModal from "./EventFormModal";
import UploadEventsModal from "./UploadEventsModal";
import ResultsEventModal from "./ResultsEventModal";

import AdminPageHeader from "./components/AdminPageHeader";
import EventFiltersBar from "./components/EventFiltersBar";
import EventList from "./components/EventList";
import PaginationBar from "./components/PaginationBar";

import { PAGE_SIZE } from "./data/eventOptions";

import useDebouncedValue from "./hooks/useDebouncedValue";

import {
  eventHasAgeCategory,
  isValidDate,
  normalizeText,
  uniqueSortedValues,
} from "./utils/eventUtils";

const getDateTimestamp = (value, endOfDay = false) => {
  if (!value) return null;

  const suffix = endOfDay
    ? "T23:59:59.999"
    : "T00:00:00.000";

  const timestamp = new Date(`${value}${suffix}`).getTime();

  return Number.isNaN(timestamp) ? null : timestamp;
};

export default function CalendrierAdmin() {
  useScrollToTop();

  const { addNotification } = useNotification();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);

  const [disciplineEvent, setDisciplineEvent] =
    useState("");

  const [ageCategory, setAgeCategory] = useState("");

  const [organisateur, setOrganisateur] =
    useState("");

  const [typeEvent, setTypeEvent] = useState("");
  const [levelEvent, setLevelEvent] = useState("");

  const [status, setStatus] = useState("all");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [uploadOpen, setUploadOpen] = useState(false);

  const [resultsOpen, setResultsOpen] =
    useState(false);

  const [resultsEvent, setResultsEvent] =
    useState(null);

  const [hasResultsMap, setHasResultsMap] =
    useState({});

  const [page, setPage] = useState(1);

  const fetchEvents = async () => {
    setLoading(true);

    try {
      const response = await getAllEvents();
      const data = Array.isArray(response)
        ? response
        : response?.data;

      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);

      addNotification(
        "Erreur lors du chargement des événements.",
        "error"
      );

      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshResultPresence = async (eventId) => {
    if (!eventId) return;

    try {
      const response =
        await getResultsByEventId(eventId);

      const rows = Array.isArray(response)
        ? response
        : response?.data;

      setHasResultsMap((previousMap) => ({
        ...previousMap,
        [eventId]:
          Array.isArray(rows) && rows.length > 0,
      }));
    } catch (error) {
      console.error(
        "Impossible de vérifier les résultats :",
        error
      );
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const allDisciplines = useMemo(
    () =>
      uniqueSortedValues(
        events.map((event) => event.discipline_event)
      ),
    [events]
  );

  const allOrganisateurs = useMemo(
    () =>
      uniqueSortedValues(
        events.map((event) => event.organisateur)
      ),
    [events]
  );

  const allTypes = useMemo(
    () =>
      uniqueSortedValues(
        events.map((event) => event.type_event)
      ),
    [events]
  );

  const allLevels = useMemo(
    () =>
      uniqueSortedValues(
        events.map((event) => event.level_event)
      ),
    [events]
  );

  const filteredEvents = useMemo(() => {
    let filtered = [...events];

    if (status !== "all") {
      filtered = filtered.filter((event) =>
        status === "active"
          ? Boolean(event.is_active)
          : !Boolean(event.is_active)
      );
    }

    if (disciplineEvent) {
      const selectedDiscipline =
        normalizeText(disciplineEvent);

      filtered = filtered.filter(
        (event) =>
          normalizeText(event.discipline_event) ===
          selectedDiscipline
      );
    }

    if (ageCategory) {
      filtered = filtered.filter((event) =>
        eventHasAgeCategory(event, ageCategory)
      );
    }

    if (organisateur) {
      const selectedOrganisateur =
        normalizeText(organisateur);

      filtered = filtered.filter(
        (event) =>
          normalizeText(event.organisateur) ===
          selectedOrganisateur
      );
    }

    if (typeEvent) {
      const selectedType = normalizeText(typeEvent);

      filtered = filtered.filter(
        (event) =>
          normalizeText(event.type_event) ===
          selectedType
      );
    }

    if (levelEvent) {
      const selectedLevel =
        normalizeText(levelEvent);

      filtered = filtered.filter(
        (event) =>
          normalizeText(event.level_event) ===
          selectedLevel
      );
    }

    if (dateFrom) {
      const minimumTimestamp =
        getDateTimestamp(dateFrom);

      if (minimumTimestamp !== null) {
        filtered = filtered.filter(
          (event) =>
            isValidDate(event.start) &&
            new Date(event.start).getTime() >=
              minimumTimestamp
        );
      }
    }

    if (dateTo) {
      const maximumTimestamp = getDateTimestamp(
        dateTo,
        true
      );

      if (maximumTimestamp !== null) {
        filtered = filtered.filter(
          (event) =>
            isValidDate(event.start) &&
            new Date(event.start).getTime() <=
              maximumTimestamp
        );
      }
    }

    if (debouncedQ.trim()) {
      const searchValue = normalizeText(debouncedQ);

      filtered = filtered.filter((event) => {
        const searchableContent = [
          event.titre,
          event.description,
          event.discipline_event,
          event.organisateur,
          event.type_event,
          event.level_event,
          event.cate_event,
          event.lieu_event,
          event.horaire_event,
        ]
          .map((value) => normalizeText(value))
          .join(" ");

        return searchableContent.includes(searchValue);
      });
    }

    filtered.sort((eventA, eventB) => {
      const dateA = isValidDate(eventA.start)
        ? new Date(eventA.start).getTime()
        : Number.MAX_SAFE_INTEGER;

      const dateB = isValidDate(eventB.start)
        ? new Date(eventB.start).getTime()
        : Number.MAX_SAFE_INTEGER;

      if (dateA !== dateB) {
        return dateA - dateB;
      }

      return String(eventA.titre || "").localeCompare(
        String(eventB.titre || ""),
        "fr"
      );
    });

    return filtered;
  }, [
    events,
    debouncedQ,
    disciplineEvent,
    ageCategory,
    organisateur,
    typeEvent,
    levelEvent,
    status,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedQ,
    disciplineEvent,
    ageCategory,
    organisateur,
    typeEvent,
    levelEvent,
    status,
    dateFrom,
    dateTo,
  ]);

  const totalEvents = events.length;

  const activeCount = events.filter((event) =>
    Boolean(event.is_active)
  ).length;

  const inactiveCount = totalEvents - activeCount;

  const now = Date.now();

  const upcomingCount = events.filter(
    (event) =>
      isValidDate(event.start) &&
      new Date(event.start).getTime() >= now
  ).length;

  const totalPages = Math.max(
    1,
    Math.ceil(filteredEvents.length / PAGE_SIZE)
  );

  const safePage = Math.min(page, totalPages);

  const pageItems = useMemo(() => {
    const startIndex =
      (safePage - 1) * PAGE_SIZE;

    return filteredEvents.slice(
      startIndex,
      startIndex + PAGE_SIZE
    );
  }, [filteredEvents, safePage]);

  useEffect(() => {
    const eventIdsToCheck = pageItems
      .map((event) => event.id)
      .filter(
        (eventId) =>
          hasResultsMap[eventId] === undefined
      );

    if (eventIdsToCheck.length === 0) return;

    const loadResultPresence = async () => {
      const entries = await Promise.all(
        eventIdsToCheck.map(async (eventId) => {
          try {
            const response =
              await getResultsByEventId(eventId);

            const rows = Array.isArray(response)
              ? response
              : response?.data;

            return [
              eventId,
              Array.isArray(rows) &&
                rows.length > 0,
            ];
          } catch {
            return [eventId, false];
          }
        })
      );

      setHasResultsMap((previousMap) => {
        const nextMap = { ...previousMap };

        entries.forEach(([eventId, hasResults]) => {
          nextMap[eventId] = hasResults;
        });

        return nextMap;
      });
    };

    loadResultPresence();
  }, [pageItems, hasResultsMap]);

  const resetFilters = () => {
    setQ("");
    setDisciplineEvent("");
    setAgeCategory("");
    setOrganisateur("");
    setTypeEvent("");
    setLevelEvent("");
    setStatus("all");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (event) => {
    setEditing(event);
    setFormOpen(true);
  };

  const openResultsModal = (event) => {
    setResultsEvent(event);
    setResultsOpen(true);
  };

  const handleSubmitForm = async (payload) => {
    try {
      if (editing?.id) {
        await updateEvent(editing.id, payload);

        addNotification(
          "Événement mis à jour.",
          "success"
        );
      } else {
        await createEvent(payload);

        addNotification(
          "Événement créé.",
          "success"
        );
      }

      setFormOpen(false);
      setEditing(null);

      await fetchEvents();
    } catch (error) {
      console.error(error);

      const message =
        error?.response?.data?.message ||
        "Erreur lors de l’enregistrement de l’événement.";

      addNotification(message, "error");
    }
  };

  const handleDuplicate = (event) => {
    const clone = {
      ...event,
      titre: event.titre
        ? `${event.titre} - Copie`
        : "",
      start: "",
      end: "",
      is_active: false,
    };

    delete clone.id;

    setEditing(clone);
    setFormOpen(true);
  };

  const handleToggleActive = async (event) => {
    try {
      const response =
        await toggleEventActive(event.id);

      const nextStatus =
        typeof response?.is_active === "boolean"
          ? response.is_active
          : typeof response?.data?.is_active ===
              "boolean"
            ? response.data.is_active
            : !Boolean(event.is_active);

      setEvents((previousEvents) =>
        previousEvents.map((currentEvent) =>
          currentEvent.id === event.id
            ? {
                ...currentEvent,
                is_active: nextStatus,
              }
            : currentEvent
        )
      );

      addNotification(
        nextStatus
          ? "Événement activé."
          : "Événement désactivé.",
        "success"
      );
    } catch (error) {
      console.error(error);

      addNotification(
        "Erreur lors du changement de statut.",
        "error"
      );
    }
  };

  const handleDelete = async (event) => {
    const confirmed = window.confirm(
      `Supprimer définitivement l'événement « ${
        event.titre || "Sans titre"
      } » ?`
    );

    if (!confirmed) return;

    try {
      await deleteEvent(event.id);

      setEvents((previousEvents) =>
        previousEvents.filter(
          (currentEvent) =>
            currentEvent.id !== event.id
        )
      );

      setHasResultsMap((previousMap) => {
        const nextMap = { ...previousMap };
        delete nextMap[event.id];
        return nextMap;
      });

      addNotification(
        "Événement supprimé.",
        "success"
      );
    } catch (error) {
      console.error(error);

      addNotification(
        "Erreur lors de la suppression.",
        "error"
      );
    }
  };

  const handleChangeImage = async (event) => {
    const input = document.createElement("input");

    input.type = "file";
    input.accept =
      "image/png,image/jpeg,image/jpg,image/webp";

    input.onchange = async () => {
      const file = input.files?.[0];

      if (!file) return;

      try {
        await updateEventImage(
          event.id,
          file,
          localStorage.getItem("token")
        );

        addNotification(
          "Image de l’événement mise à jour.",
          "success"
        );

        await fetchEvents();
      } catch (error) {
        console.error(error);

        addNotification(
          "Erreur lors de la mise à jour de l’image.",
          "error"
        );
      }
    };

    input.click();
  };

  const openCastingPage = (event) => {
    if (!event?.id) return;

    try {
      localStorage.setItem(
        `casting_event_${event.id}`,
        JSON.stringify(event)
      );
    } catch (error) {
      console.error(
        "Impossible d'enregistrer l'événement pour le casting :",
        error
      );
    }

    const url = `/casting/${encodeURIComponent(
      event.id
    )}`;

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="w-full pb-10 pt-[96px] font-satoshi">
      <div className="mx-auto max-w-[1500px] px-4">
        <AdminPageHeader
          totalEvents={totalEvents}
          activeCount={activeCount}
          inactiveCount={inactiveCount}
          upcomingCount={upcomingCount}
        />

        <div className="mt-5">
          <EventFiltersBar
            q={q}
            setQ={setQ}
            disciplineEvent={disciplineEvent}
            setDisciplineEvent={
              setDisciplineEvent
            }
            ageCategory={ageCategory}
            setAgeCategory={setAgeCategory}
            organisateur={organisateur}
            setOrganisateur={setOrganisateur}
            typeEvent={typeEvent}
            setTypeEvent={setTypeEvent}
            levelEvent={levelEvent}
            setLevelEvent={setLevelEvent}
            status={status}
            setStatus={setStatus}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            allDisciplines={allDisciplines}
            allOrganisateurs={allOrganisateurs}
            allTypes={allTypes}
            allLevels={allLevels}
            onReset={resetFilters}
            onRefresh={fetchEvents}
            onUpload={() => setUploadOpen(true)}
            onCreate={openCreate}
            loading={loading}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {filteredEvents.length} événement
            {filteredEvents.length > 1 ? "s" : ""}
          </span>

          {disciplineEvent ? (
            <span className="rounded-full bg-violet-50 px-3 py-1 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
              Discipline : {disciplineEvent}
            </span>
          ) : null}

          {ageCategory ? (
            <span className="rounded-full bg-sky-50 px-3 py-1 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
              Catégorie : {ageCategory}
            </span>
          ) : null}

          {organisateur ? (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Organisateur : {organisateur}
            </span>
          ) : null}

          {typeEvent ? (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Type : {typeEvent}
            </span>
          ) : null}

          {levelEvent ? (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Niveau : {levelEvent}
            </span>
          ) : null}

          {status !== "all" ? (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Statut :{" "}
              {status === "active"
                ? "Actifs"
                : "Inactifs"}
            </span>
          ) : null}

          {debouncedQ ? (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Recherche : {q}
            </span>
          ) : null}

          {!loading &&
          filteredEvents.length > 0 ? (
            <span className="ml-auto text-xs">
              Affichage de {pageItems.length} élément
              {pageItems.length > 1 ? "s" : ""} • page{" "}
              {safePage}/{totalPages}
            </span>
          ) : null}
        </div>

        <div className="mt-5">
          <EventList
            rows={pageItems}
            loading={loading}
            hasResultsMap={hasResultsMap}
            onToggleActive={handleToggleActive}
            onEdit={openEdit}
            onDuplicate={handleDuplicate}
            onResults={openResultsModal}
            onCasting={openCastingPage}
            onImage={handleChangeImage}
            onDelete={handleDelete}
          />
        </div>

        <PaginationBar
          page={safePage}
          pageCount={totalPages}
          setPage={setPage}
          loading={loading}
        />
      </div>

      <EventFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        initialData={editing}
        onSubmit={handleSubmitForm}
      />

      <UploadEventsModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={fetchEvents}
      />

      <ResultsEventModal
        isOpen={resultsOpen}
        event={resultsEvent}
        onClose={async () => {
          const eventId = resultsEvent?.id;

          setResultsOpen(false);
          setResultsEvent(null);

          if (eventId) {
            await refreshResultPresence(eventId);
          }
        }}
      />
    </div>
  );
}